import { TTS_CONFIG } from '@/constants';
import type { TTSOptions } from '@/types';

class TTSService {
  private synthesis: SpeechSynthesis | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
      this.initVoices();
    }
  }

  private initVoices(): void {
    if (!this.synthesis) return;

    const loadVoices = () => {
      this.voices = this.synthesis!.getVoices();
      this.isInitialized = true;
    };

    // Load voices
    loadVoices();

    // Chrome loads voices asynchronously
    if (this.synthesis.onvoiceschanged !== undefined) {
      this.synthesis.onvoiceschanged = loadVoices;
    }
  }

  private getPreferredVoice(): SpeechSynthesisVoice | undefined {
    if (this.voices.length === 0) return undefined;

    // Try to find a preferred voice
    for (const preferredName of TTS_CONFIG.PREFERRED_VOICES) {
      const voice = this.voices.find((v) => v.name.includes(preferredName));
      if (voice) return voice;
    }

    // Fallback to first English voice
    const englishVoice = this.voices.find((v) => v.lang.startsWith('en'));
    return englishVoice || this.voices[0];
  }

  speak(
    text: string,
    options: TTSOptions = {},
    onStart?: () => void,
    onEnd?: () => void,
    onError?: (error: Error) => void
  ): void {
    if (!this.synthesis) {
      onError?.(new Error('Speech synthesis not supported'));
      return;
    }

    // Cancel any ongoing speech
    this.stop();

    // Wait for voices to be loaded
    if (!this.isInitialized) {
      setTimeout(() => this.speak(text, options, onStart, onEnd, onError), 100);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);

    // Set voice
    const voice = options.voice || this.getPreferredVoice();
    if (voice) {
      utterance.voice = voice;
    }

    // Set options
    utterance.rate = options.rate ?? TTS_CONFIG.DEFAULT_RATE;
    utterance.pitch = options.pitch ?? TTS_CONFIG.DEFAULT_PITCH;
    utterance.volume = options.volume ?? TTS_CONFIG.DEFAULT_VOLUME;

    // Event handlers
    utterance.onstart = () => {
      onStart?.();
    };

    utterance.onend = () => {
      onEnd?.();
    };

    utterance.onerror = (event) => {
      console.error('TTS Error:', event);
      onError?.(new Error(event.error));
    };

    this.synthesis.speak(utterance);
  }

  stop(): void {
    if (this.synthesis) {
      this.synthesis.cancel();
    }
  }

  pause(): void {
    if (this.synthesis && this.synthesis.speaking) {
      this.synthesis.pause();
    }
  }

  resume(): void {
    if (this.synthesis && this.synthesis.paused) {
      this.synthesis.resume();
    }
  }

  isSpeaking(): boolean {
    return this.synthesis?.speaking ?? false;
  }

  isPaused(): boolean {
    return this.synthesis?.paused ?? false;
  }

  isSupported(): boolean {
    return this.synthesis !== null;
  }

  getVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }

  getVoiceByIndex(index: number): SpeechSynthesisVoice | undefined {
    return this.voices[index];
  }

  getEnglishVoices(): SpeechSynthesisVoice[] {
    return this.voices.filter((v) => v.lang.startsWith('en'));
  }
}

export const ttsService = new TTSService();
